import { useState, useEffect, createContext, useContext } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
    const shouldBeDark = savedTheme === 'dark' || (!savedTheme && prefersDark);
    
    setIsDark(shouldBeDark);
    updateTheme(shouldBeDark);
  }, []);

  const updateTheme = (dark) => {
    const root = document.documentElement;
    
    if (dark) {
      root.classList?.add('dark');
      // Dark mode color variables
      root.style?.setProperty('--color-background', '#0F172A'); // slate-900
      root.style?.setProperty('--color-foreground', '#F1F5F9'); // slate-100
      root.style?.setProperty('--color-border', '#334155'); // slate-700
      root.style?.setProperty('--color-input', '#1E293B'); // slate-800
      root.style?.setProperty('--color-card', '#1E293B'); // slate-800
      root.style?.setProperty('--color-card-foreground', '#F1F5F9'); // slate-100
      root.style?.setProperty('--color-muted', '#334155'); // slate-700
      root.style?.setProperty('--color-muted-foreground', '#94A3B8'); // slate-400
      root.style?.setProperty('--color-popover', '#1E293B'); // slate-800
      root.style?.setProperty('--color-popover-foreground', '#F1F5F9'); // slate-100
    } else {
      root.classList?.remove('dark');
      // Light mode color variables (restore defaults)
      root.style?.setProperty('--color-background', '#F8FAFC'); // slate-50
      root.style?.setProperty('--color-foreground', '#0F172A'); // slate-900
      root.style?.setProperty('--color-border', '#E2E8F0'); // slate-200
      root.style?.setProperty('--color-input', '#FFFFFF'); // white
      root.style?.setProperty('--color-card', '#FFFFFF'); // white
      root.style?.setProperty('--color-card-foreground', '#0F172A'); // slate-900
      root.style?.setProperty('--color-muted', '#F1F5F9'); // slate-100
      root.style?.setProperty('--color-muted-foreground', '#475569'); // slate-600
      root.style?.setProperty('--color-popover', '#FFFFFF'); // white
      root.style?.setProperty('--color-popover-foreground', '#0F172A'); // slate-900
    }
  };

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    updateTheme(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default useTheme;